import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Zap, Settings2, ArrowRight, HelpCircle, Trash2, Plus } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = {
  maxDebtRatio: 50, minimumIncome: 5000, maximumLoan: 2000, maximumFailedDebits: 2,
  declineOnArrears: true, declineOnHandedOver: true, excludeMonthlySettleDebt: true,
};

const DEFAULT_QUESTIONS = {
  builtin: {
    salary: { label: 'Monthly Take-Home Salary', tip: 'The amount actually paid into your bank account every month after tax and deductions — your take-home (nett) pay.', enabled: true, core: true },
    existingDebt: { label: 'Existing Monthly Continuing Debt', tip: 'The total you pay every month on debts that carry on next month — e.g. store accounts, car finance, cellphone contracts and personal loan instalments.', enabled: true, core: false },
    monthlySettleDebt: { label: 'Current Monthly-Settle Loans', tip: 'Short-term micro loans you will fully pay off (settle) this month. Because they end this month, they are not counted against next month\u2019s affordability.', enabled: true, core: false },
    loanAmount: { label: 'Requested Loan Amount', tip: 'How much money you would like to borrow from 2k2day.', enabled: true, core: true },
    newLoanRepayment: { label: 'Total Repayment on Requested Loan', tip: 'The full amount you will pay back on this new loan, including interest and fees. Example: borrow R1,000 and pay back R1,300 — enter 1300.', enabled: true, core: true },
    arrears: { label: 'Any Active Micro Loan Arrears (Last 12 Months)?', tip: 'Choose Yes if you were behind on payments (missed or late instalments) on any micro loan at any point in the last 12 months.', enabled: true, core: false },
    handedOver: { label: 'Any Micro Loan Handed-Over Accounts (Last 12 Months)?', tip: 'Choose Yes if any micro loan account was handed over to debt collectors or attorneys in the last 12 months.', enabled: true, core: false },
    failedDebits: { label: 'Recent Failed Debit Orders?', tip: 'How many debit orders bounced or were returned unpaid on your bank account in the last few months.', enabled: true, core: false },
  },
  custom: [],
};

const fmt = (n) => `R${Number(n || 0).toFixed(2)}`;

const InfoTip = ({ tip, name }) => {
  if (!tip) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-label="What does this mean?" data-testid={`preapproval-tip-${name}`}
          className="inline-flex items-center text-slate-400 hover:text-emerald-700 align-middle ml-1 shrink-0">
          <HelpCircle className="w-3.5 h-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" className="max-w-[260px] p-3 text-xs text-slate-600 leading-relaxed" data-testid={`preapproval-tip-content-${name}`}>
        {tip}
      </PopoverContent>
    </Popover>
  );
};

const NumField = ({ q, name, placeholder, form, set }) => (
  <div className="space-y-1">
    <Label className="text-xs font-semibold text-slate-600">{q.label}<InfoTip tip={q.tip} name={name} /></Label>
    <Input type="text" inputMode="decimal" autoComplete="off" value={form[name]} placeholder={placeholder}
      onChange={e => set(name, e.target.value.replace(/[^0-9.]/g, ''))} data-testid={`preapproval-${name}`} />
  </div>
);

const YesNoField = ({ label, tip, name, value, onChange }) => (
  <div className="space-y-1">
    <Label className="text-xs font-semibold text-slate-600">{label}<InfoTip tip={tip} name={name} /></Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={`preapproval-${name}`}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes</SelectItem></SelectContent>
    </Select>
  </div>
);

export const PreApprovalCheck = ({ showSettings = false, showApplyCta = true, getAuthHeader }) => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(DEFAULTS);
  const [questions, setQuestions] = useState(DEFAULT_QUESTIONS);
  const [form, setForm] = useState({
    salary: '', existingDebt: '', monthlySettleDebt: '', loanAmount: '',
    newLoanRepayment: '', arrears: 'no', handedOver: 'no', failedDebits: '0',
  });
  const [customAnswers, setCustomAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [editSettings, setEditSettings] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState(DEFAULTS);
  const [questionsDraft, setQuestionsDraft] = useState(DEFAULT_QUESTIONS);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/pre-approval-settings`)
      .then(res => { setSettings(res.data); setSettingsDraft(res.data); })
      .catch(() => {});
    axios.get(`${API_URL}/pre-approval-questions`)
      .then(res => { setQuestions(res.data); setQuestionsDraft(JSON.parse(JSON.stringify(res.data))); })
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const Q = questions.builtin;
  const on = (k) => Q[k]?.enabled !== false;
  const activeCustom = (questions.custom || []).filter(c => c.enabled !== false);

  const track = (event, resultType, extra = {}) => {
    if (!showApplyCta) return;
    axios.post(`${API_URL}/track/preapproval`, { event, result: resultType, ...extra }).catch(() => {});
  };

  const buildAnswers = () => {
    const answers = {};
    if (on('arrears')) answers[Q.arrears.label] = form.arrears;
    if (on('handedOver')) answers[Q.handedOver.label] = form.handedOver;
    if (on('failedDebits')) answers[Q.failedDebits.label] = form.failedDebits;
    activeCustom.forEach(c => { answers[c.label] = customAnswers[c.id] || 'no'; });
    return answers;
  };

  const finishResult = (r, counted = true, reason = '') => {
    setResult(r);
    if (counted) track('check', r.type, { reason, answers: buildAnswers() });
  };

  const check = () => {
    const S = settings;
    const salary = Number(form.salary);
    const existingDebt = on('existingDebt') ? (Number(form.existingDebt) || 0) : 0;
    const monthlySettleDebt = on('monthlySettleDebt') ? (Number(form.monthlySettleDebt) || 0) : 0;
    const loanAmount = Number(form.loanAmount);
    const newLoanRepayment = Number(form.newLoanRepayment);
    const failedDebits = Number(form.failedDebits);

    if (salary <= 0 || loanAmount <= 0 || newLoanRepayment <= 0) {
      finishResult({ type: 'declined', title: 'Missing Information', lines: ['Please complete all required fields.'] }, false);
      return;
    }
    if (salary < S.minimumIncome) {
      finishResult({
        type: 'declined', title: 'Currently Not Eligible',
        lines: ['Your stated income does not meet our current minimum pre-qualification requirement.'],
        small: 'This is a preliminary result only.',
      }, true, 'Income below minimum');
      return;
    }
    if (loanAmount > S.maximumLoan) {
      finishResult({
        type: 'declined', title: 'Requested Amount Too High',
        lines: [`The maximum loan currently available is ${fmt(S.maximumLoan)}.`],
      }, true, 'Amount above maximum');
      return;
    }
    if (S.declineOnHandedOver && on('handedOver') && form.handedOver === 'yes') {
      finishResult({
        type: 'declined', title: 'Currently Not Eligible',
        lines: ['Based on the information provided, we are unable to provide a pre-approval at this time.'],
      }, true, 'Handed-over account');
      return;
    }
    const customDecline = activeCustom.find(c => c.action === 'decline' && customAnswers[c.id] === 'yes');
    if (customDecline) {
      finishResult({
        type: 'declined', title: 'Currently Not Eligible',
        lines: ['Based on the information provided, we are unable to provide a pre-approval at this time.'],
      }, true, customDecline.label);
      return;
    }
    if (S.declineOnArrears && on('arrears') && form.arrears === 'yes') {
      finishResult({
        type: 'possible', title: 'Further Assessment Required',
        lines: ['We need to review your current accounts before determining whether you qualify.',
          'You may continue with the full application.'],
      }, true, 'Micro loan arrears');
      return;
    }
    const customAssess = activeCustom.find(c => c.action === 'assess' && customAnswers[c.id] === 'yes');
    if (customAssess) {
      finishResult({
        type: 'possible', title: 'Further Assessment Required',
        lines: ['Your application requires a full affordability review.', 'You may continue with the full application.'],
      }, true, customAssess.label);
      return;
    }
    if (on('failedDebits') && failedDebits > S.maximumFailedDebits) {
      finishResult({
        type: 'possible', title: 'Further Assessment Required',
        lines: ['Your application requires a full affordability review.'],
      }, true, 'Failed debit orders');
      return;
    }

    let futureExistingDebt = existingDebt;
    if (!S.excludeMonthlySettleDebt) futureExistingDebt += monthlySettleDebt;
    const projectedDebt = futureExistingDebt + newLoanRepayment;
    const projectedDebtRatio = (projectedDebt / salary) * 100;
    const maximumDebtAllowed = salary * (S.maxDebtRatio / 100);
    const availableCapacity = Math.max(maximumDebtAllowed - futureExistingDebt, 0);

    if (projectedDebtRatio <= S.maxDebtRatio) {
      finishResult({
        type: 'approved', title: 'Pre-Approved', amount: loanAmount,
        lines: [
          `Estimated total repayment: ${fmt(newLoanRepayment)}`,
          `Projected debt ratio: ${projectedDebtRatio.toFixed(1)}%`,
          `Available repayment capacity: ${fmt(availableCapacity)}`,
        ],
        small: 'This is a preliminary pre-approval only. Final approval is subject to verification of income, bank statements, credit information and affordability.',
      }, true, 'Approved');
    } else {
      finishResult({
        type: 'declined', title: 'Currently Not Eligible',
        lines: [
          'The requested loan would exceed the current affordability limit.',
          `Maximum additional repayment capacity: ${fmt(availableCapacity)}`,
        ],
        small: 'You may qualify for a smaller amount.',
      }, true, 'Debt ratio too high');
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const [s, q] = await Promise.all([
        axios.put(`${API_URL}/admin/pre-approval-settings`, settingsDraft, getAuthHeader()),
        axios.put(`${API_URL}/admin/pre-approval-questions`, questionsDraft, getAuthHeader()),
      ]);
      setSettings(s.data);
      setQuestions(q.data);
      setQuestionsDraft(JSON.parse(JSON.stringify(q.data)));
      setEditSettings(false);
      toast.success('Pre-approval rules & questions saved');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save'); }
    finally { setSavingSettings(false); }
  };

  const setBuiltinDraft = (k, field, v) =>
    setQuestionsDraft(p => ({ ...p, builtin: { ...p.builtin, [k]: { ...p.builtin[k], [field]: v } } }));
  const setCustomDraft = (i, field, v) =>
    setQuestionsDraft(p => ({ ...p, custom: p.custom.map((c, idx) => idx === i ? { ...c, [field]: v } : c) }));

  return (
    <div className="space-y-4" data-testid="preapproval-check">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {on('salary') && <NumField q={Q.salary} name="salary" placeholder="Example: 10000" form={form} set={set} />}
        {on('existingDebt') && <NumField q={Q.existingDebt} name="existingDebt" placeholder="Example: 2000" form={form} set={set} />}
        {on('monthlySettleDebt') && <NumField q={Q.monthlySettleDebt} name="monthlySettleDebt" placeholder="Example: 1000" form={form} set={set} />}
        {on('loanAmount') && <NumField q={Q.loanAmount} name="loanAmount" placeholder="Example: 1000" form={form} set={set} />}
        {on('newLoanRepayment') && <NumField q={Q.newLoanRepayment} name="newLoanRepayment" placeholder="Example: 1300" form={form} set={set} />}
        {on('arrears') && <YesNoField label={Q.arrears.label} tip={Q.arrears.tip} name="arrears" value={form.arrears} onChange={v => set('arrears', v)} />}
        {on('handedOver') && <YesNoField label={Q.handedOver.label} tip={Q.handedOver.tip} name="handedOver" value={form.handedOver} onChange={v => set('handedOver', v)} />}
        {on('failedDebits') && (
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-slate-600">{Q.failedDebits.label}<InfoTip tip={Q.failedDebits.tip} name="failedDebits" /></Label>
            <Select value={form.failedDebits} onValueChange={v => set('failedDebits', v)}>
              <SelectTrigger data-testid="preapproval-failedDebits"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0">None</SelectItem>
                <SelectItem value="1">1</SelectItem>
                <SelectItem value="2">2</SelectItem>
                <SelectItem value="3">3 or more</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {activeCustom.map(c => (
          <YesNoField key={c.id} label={c.label} tip={c.tip} name={`custom-${c.id}`}
            value={customAnswers[c.id] || 'no'} onChange={v => setCustomAnswers(p => ({ ...p, [c.id]: v }))} />
        ))}
      </div>

      <Button onClick={check} className="w-full bg-emerald-900 hover:bg-emerald-800 rounded-full h-12 text-base" data-testid="preapproval-check-btn">
        <Zap className="w-4 h-4 mr-2" /> Check Pre-Approval
      </Button>

      {result && (
        <div data-testid="preapproval-result" className={`rounded-xl p-5 border ${
          result.type === 'approved' ? 'bg-emerald-50 border-emerald-200' :
          result.type === 'possible' ? 'bg-amber-50 border-amber-200' :
          'bg-red-50 border-red-200'
        }`}>
          <h3 className={`font-bold text-lg ${
            result.type === 'approved' ? 'text-emerald-800' :
            result.type === 'possible' ? 'text-amber-800' : 'text-red-800'
          }`}>{result.type === 'approved' ? '✅ ' : ''}{result.title}</h3>
          {result.type === 'approved' && (
            <>
              <p className="text-sm text-slate-600 mt-1">Based on the information provided, you may qualify for:</p>
              <p className="text-3xl font-bold text-emerald-900 my-2 tabular-nums">{fmt(result.amount)}</p>
            </>
          )}
          <div className="space-y-1 mt-2">
            {result.lines.map((l, i) => <p key={i} className="text-sm text-slate-700">{l}</p>)}
          </div>
          {result.small && <p className="text-xs text-slate-500 mt-3">{result.small}</p>}
          {result.type === 'approved' && showApplyCta && (
            <Button className="w-full mt-4 bg-emerald-900 hover:bg-emerald-800 rounded-full h-11" data-testid="preapproval-apply-btn"
              onClick={() => { track('continue'); navigate(`/apply?amount=${result.amount}`); }}>
              Continue to full application <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      )}

      {showSettings && (
        <div className="border-t border-slate-200 pt-3">
          <button className="text-xs text-slate-500 flex items-center gap-1 hover:text-slate-700" data-testid="preapproval-settings-toggle"
            onClick={() => setEditSettings(v => !v)}>
            <Settings2 className="w-3.5 h-3.5" /> {editSettings ? 'Hide' : 'Edit'} rules & questions
          </button>
          {editSettings && (
            <div className="mt-3 space-y-5">
              <div>
                <p className="text-xs font-bold text-slate-700 uppercase mb-2">Rules</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[['maxDebtRatio', 'Max Debt Ratio %'], ['minimumIncome', 'Minimum Income'], ['maximumLoan', 'Maximum Loan'], ['maximumFailedDebits', 'Max Failed Debits']].map(([k, label]) => (
                    <div key={k} className="space-y-1">
                      <Label className="text-[11px] text-slate-500">{label}</Label>
                      <Input type="number" value={settingsDraft[k]} data-testid={`preapproval-setting-${k}`}
                        onChange={e => setSettingsDraft(p => ({ ...p, [k]: Number(e.target.value) }))} />
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-4 mt-3">
                  {[['declineOnArrears', 'Decline on arrears'], ['declineOnHandedOver', 'Decline on handed-over'], ['excludeMonthlySettleDebt', 'Exclude monthly-settle debt']].map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2 text-xs text-slate-600">
                      <Switch checked={!!settingsDraft[k]} onCheckedChange={v => setSettingsDraft(p => ({ ...p, [k]: v }))} data-testid={`preapproval-setting-${k}`} />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-bold text-slate-700 uppercase mb-2">Questions — edit wording, help text, or hide</p>
                <div className="space-y-2">
                  {Object.entries(questionsDraft.builtin).map(([k, q]) => (
                    <div key={k} className={`rounded-lg border p-2.5 space-y-2 ${q.enabled === false ? 'bg-slate-50 opacity-70' : 'bg-white'}`} data-testid={`question-editor-${k}`}>
                      <div className="flex items-center gap-2">
                        <Input className="h-8 text-xs flex-1" value={q.label}
                          onChange={e => setBuiltinDraft(k, 'label', e.target.value)} data-testid={`question-label-${k}`} />
                        <label className="flex items-center gap-1.5 text-[11px] text-slate-500 shrink-0">
                          <Switch checked={q.enabled !== false} disabled={q.core}
                            onCheckedChange={v => setBuiltinDraft(k, 'enabled', v)} data-testid={`question-enabled-${k}`} />
                          {q.core ? 'Required' : (q.enabled !== false ? 'Shown' : 'Hidden')}
                        </label>
                      </div>
                      <Input className="h-8 text-[11px] text-slate-500" value={q.tip} placeholder="Help-pin explanation"
                        onChange={e => setBuiltinDraft(k, 'tip', e.target.value)} data-testid={`question-tip-${k}`} />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-bold text-slate-700 uppercase mb-2">Your own Yes/No questions</p>
                <div className="space-y-2">
                  {questionsDraft.custom.map((c, i) => (
                    <div key={c.id || i} className="rounded-lg border p-2.5 space-y-2 bg-white" data-testid={`custom-question-${i}`}>
                      <div className="flex items-center gap-2">
                        <Input className="h-8 text-xs flex-1" value={c.label} placeholder="Question, e.g. Are you under debt review?"
                          onChange={e => setCustomDraft(i, 'label', e.target.value)} data-testid={`custom-question-label-${i}`} />
                        <Select value={c.action} onValueChange={v => setCustomDraft(i, 'action', v)}>
                          <SelectTrigger className="h-8 w-[170px] text-xs shrink-0" data-testid={`custom-question-action-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="decline">Decline if Yes</SelectItem>
                            <SelectItem value="assess">Needs assessment if Yes</SelectItem>
                            <SelectItem value="info">Info only</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button variant="ghost" size="sm" className="text-red-500 h-8 px-2 shrink-0" data-testid={`custom-question-delete-${i}`}
                          onClick={() => setQuestionsDraft(p => ({ ...p, custom: p.custom.filter((_, idx) => idx !== i) }))}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                      <Input className="h-8 text-[11px] text-slate-500" value={c.tip || ''} placeholder="Help-pin explanation (optional)"
                        onChange={e => setCustomDraft(i, 'tip', e.target.value)} data-testid={`custom-question-tip-${i}`} />
                    </div>
                  ))}
                </div>
                <Button variant="outline" size="sm" className="mt-2" data-testid="add-custom-question-btn"
                  onClick={() => setQuestionsDraft(p => ({ ...p, custom: [...p.custom, { id: `q_${Date.now()}`, label: '', tip: '', action: 'info', enabled: true }] }))}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add question
                </Button>
              </div>

              <Button size="sm" onClick={saveSettings} disabled={savingSettings} data-testid="preapproval-settings-save">
                {savingSettings ? 'Saving...' : 'Save Rules & Questions'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PreApprovalCheck;
